import { Fragment, useMemo, useState } from 'react';
import { Popover, Transition } from '@headlessui/react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isValid,
  parse,
  startOfMonth,
  startOfWeek,
  subMonths,
} from 'date-fns';
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

const VALUE_FORMAT = 'yyyy-MM-dd';
const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

export interface DatePickerProps {
  /** Selected value as an ISO date string (yyyy-MM-dd). Empty string means no selection. */
  value: string;
  /** Called with the selected ISO date string (yyyy-MM-dd). */
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  /** Accessible name for the trigger when no visible label is provided. */
  ariaLabel?: string;
}

/** Parse a yyyy-MM-dd string into a local Date, or null when empty/invalid. */
function parseValue(value: string): Date | null {
  if (!value) {
    return null;
  }
  const parsed = parse(value, VALUE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
}

const triggerClasses =
  'flex h-10 w-full items-center justify-between rounded-lg border border-secondary-300 bg-white px-3 text-sm ring-offset-white transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-secondary-600 dark:bg-secondary-800 dark:ring-offset-secondary-900';

export function DatePicker({
  value,
  onChange,
  label,
  required,
  placeholder = 'Select date',
  error,
  disabled,
  ariaLabel,
}: DatePickerProps) {
  const selected = useMemo(() => parseValue(value), [value]);
  const [viewMonth, setViewMonth] = useState<Date>(() => selected ?? new Date());

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewMonth));
    const gridEnd = endOfWeek(endOfMonth(viewMonth));
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [viewMonth]);

  const today = useMemo(() => new Date(), []);

  return (
    <div className="w-full">
      {label && (
        <label className="mb-2 block text-sm font-medium text-secondary-900 dark:text-secondary-100">
          {label}
          {required && <span className="ml-1 text-danger-500">*</span>}
        </label>
      )}
      <Popover className="relative">
        {/* eslint-disable-next-line @typescript-eslint/unbound-method -- `close` is a render-prop callback supplied by Headless UI, not an unbound class method */}
        {({ close }) => (
          <>
            <Popover.Button
              type="button"
              disabled={disabled}
              aria-label={ariaLabel ?? label}
              className={cn(
                triggerClasses,
                error && 'border-danger-300 focus-visible:ring-danger-500 dark:border-danger-600',
              )}
            >
              <span className={cn(selected ? 'text-secondary-900 dark:text-white' : 'text-secondary-500 dark:text-secondary-400')}>
                {selected ? format(selected, 'MMM d, yyyy') : placeholder}
              </span>
              <CalendarDaysIcon className="h-4 w-4 text-secondary-400 dark:text-secondary-500" />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Popover.Panel className="absolute z-50 mt-2 w-72 rounded-xl border border-secondary-200 bg-white p-3 shadow-xl dark:border-secondary-700 dark:bg-secondary-900">
                <div className="mb-2 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setViewMonth((m) => subMonths(m, 1))}
                    className="rounded-md p-1 text-secondary-500 hover:bg-secondary-100 hover:text-secondary-900 dark:hover:bg-secondary-800 dark:hover:text-white"
                    aria-label="Previous month"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-sm font-semibold text-secondary-900 dark:text-white">
                    {format(viewMonth, 'MMMM yyyy')}
                  </span>
                  <button
                    type="button"
                    onClick={() => setViewMonth((m) => addMonths(m, 1))}
                    className="rounded-md p-1 text-secondary-500 hover:bg-secondary-100 hover:text-secondary-900 dark:hover:bg-secondary-800 dark:hover:text-white"
                    aria-label="Next month"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="mb-1 grid grid-cols-7 gap-1">
                  {WEEKDAYS.map((d) => (
                    <div key={d} className="text-center text-xs font-medium text-secondary-400 dark:text-secondary-500">
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {days.map((day) => {
                    const inMonth = isSameMonth(day, viewMonth);
                    const isSelected = selected ? isSameDay(day, selected) : false;
                    const isToday = isSameDay(day, today);
                    return (
                      <button
                        key={day.toISOString()}
                        type="button"
                        onClick={() => {
                          onChange(format(day, VALUE_FORMAT));
                          setViewMonth(day);
                          close();
                        }}
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-md text-sm transition',
                          inMonth ? 'text-secondary-900 dark:text-secondary-100' : 'text-secondary-300 dark:text-secondary-600',
                          !isSelected && 'hover:bg-secondary-100 dark:hover:bg-secondary-800',
                          isSelected && 'bg-primary-600 font-semibold text-white hover:bg-primary-600',
                          !isSelected && isToday && 'ring-1 ring-inset ring-primary-400',
                        )}
                      >
                        {format(day, 'd')}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-2 flex items-center justify-between border-t border-secondary-100 pt-2 dark:border-secondary-700">
                  <button
                    type="button"
                    onClick={() => {
                      onChange(format(new Date(), VALUE_FORMAT));
                      setViewMonth(new Date());
                      close();
                    }}
                    className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
                  >
                    Today
                  </button>
                  {value && (
                    <button
                      type="button"
                      onClick={() => {
                        onChange('');
                        close();
                      }}
                      className="text-xs font-medium text-secondary-500 hover:underline dark:text-secondary-400"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
      {error && <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">{error}</p>}
    </div>
  );
}
